import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import "./BookingModal.scss";
import { useLanguage } from "../../context/LanguageContext";
import { useCart } from "../../context/CartContext";
import { useAuth } from "../../context/AuthContext";
import api from "../../utils/api";
import Login from "../../pages/Login/Login";
import { LockClock } from "@mui/icons-material";
import moment from "moment";
import { monthNames, weekDays } from "../../SourceData";

const BookingModal = ({
  open,
  onClose,
  selectedDepartment,
  selectedServices,
  onBookingComplete,
  selectedAddress,

}) => {
  // ============ STATE MANAGEMENT ============
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [availableTimes, setAvailableTimes] = useState([]);
  const [bookedTimes, setBookedTimes] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [calendarDays, setCalendarDays] = useState([]);
  const [bookingAvailability, setBookingAvailability] = useState(true);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [workingHours, setWorkingHours] = useState({
    from: "09:00",
    to: "17:00",
  });
  // Add a state to keep track of the current time for UI updates
  const [currentTime, setCurrentTime] = useState(new Date());

  // ============ HOOKS ============
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { setAppointment, addServices } = useCart();
  const { isAuthenticated } = useAuth();
  const isArabic = language === "ar";

  // ============ HELPER FUNCTIONS ============
  // Helper function to check if current time is past working hours
  function isTimePastWorkingHours(currentTime, closingTimeStr) {
    const [closingHour, closingMinute] = closingTimeStr.split(":").map(Number);
    const currentHour = currentTime.getHours();
    const currentMinute = currentTime.getMinutes();

    return (
      currentHour > closingHour ||
      (currentHour === closingHour && currentMinute >= closingMinute)
    );
  }

  // Add helper function to check if a time slot is in the past
  function isTimeSlotPast(timeSlot) {
    // Only check for today
    if (
      !selectedDate ||
      selectedDate.toDateString() !== new Date().toDateString()
    ) {
      return false;
    }

    // Parse the end time of the slot
    const [hours, minutes] = timeSlot.endTime.split(":").map(Number);

    // Create a date object for the slot's end time
    const slotEndTime = new Date();
    slotEndTime.setHours(hours, minutes, 0, 0);

    // Compare with current time
    return currentTime >= slotEndTime;
  }

  // ============ TRANSLATIONS ============
  const translations = useMemo(
    () => ({
      modalTitle: isArabic ? "حجز موعد" : "Book Appointment",
      dateSelectionTitle: isArabic
        ? "اختر الموعد المناسب"
        : "Choose a Suitable Date",
      cancelButtonLabel: isArabic ? "إلغاء" : "Cancel",
      confirmButtonLabel: isArabic ? "تأكيد الحجز" : "Confirm Booking",
      availableTimeLabel: isArabic
        ? `الوقت المتاح ليوم ${
            selectedDate ? getFormattedDate(selectedDate) : ""
          }`
        : `Available times for ${
            selectedDate
              ? selectedDate.toLocaleDateString("en-US", {
                  weekday: "long",
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })
              : ""
          }`,
      fullyBookedLabel: isArabic
        ? "مواعيد محجوزة بالكامل"
        : "Fully Booked Slots",
      noAvailability: isArabic
        ? "عذرًا، لا توجد مواعيد متاحة في هذا اليوم"
        : "Sorry, no availability for this day",
      noAvailabilityToday: isArabic
        ? "عذرًا، لا يمكن الحجز بعد ساعات العمل"
        : "Sorry, no bookings after working hours",
      pleaseSelectDate: isArabic
        ? "يرجى اختيار تاريخ لعرض المواعيد المتاحة"
        : "Please select a date to see available time slots",
      loading: isArabic ? "جاري التحميل..." : "Loading...",
      errorMessage: isArabic
        ? "حدث خطأ أثناء تحميل المواعيد المتاحة. يرجى المحاولة مرة أخرى."
        : "Error loading available times. Please try again.",
      bookedStatus: isArabic ? "(محجوز)" : "(Booked)",
      pastTimeStatus: isArabic ? "(وقت مضى)" : "(Past time)",
      workingHours: isArabic ? "ساعات العمل" : "Working Hours",
    }),
    [isArabic, selectedDate]
  );

  // ============ INITIALIZE MODAL ============
  useEffect(() => {
    if (open) {
      // Store pending services if user is not authenticated
      if (!isAuthenticated && selectedServices.length > 0) {
        localStorage.setItem(
          "pendingServices",
          JSON.stringify(selectedServices)
        );
      }

      // Initialize calendar with today's date
      const today = new Date();
      setSelectedDate(today);
      setCurrentMonth(new Date(today.getFullYear(), today.getMonth(), 1));
      setCurrentTime(new Date());

      // Reset state when modal opens
      setSelectedTime(null);
      setError(null);
    }
  }, [open, isAuthenticated, selectedServices]);

  // Add interval to update current time every minute
  useEffect(() => {
    if (!open) return;

    // Initial current time setting
    setCurrentTime(new Date());

    // Set up an interval to update current time every minute
    const intervalId = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // 60000 ms = 1 minute

    // Clean up interval on unmount or when modal closes
    return () => clearInterval(intervalId);
  }, [open]);

  // ============ DATE & TIME FORMATTING ============
  function formatDateForAPI(date) {
    if (!date) return "";

    // Use these methods instead of toISOString() to preserve the local date
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const day = date.getDate().toString().padStart(2, "0");

    return `${year}-${month}-${day}`; // Returns YYYY-MM-DD in local time
  }

  function formatDate(date) {
    if (!date) return "";
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear();
    return `${year}/${month}/${day}`;
  }

  function formatTime(timeString) {
    if (!timeString || !timeString.includes(":")) {
      console.error("Invalid time format:", timeString);
      return timeString || "";
    }

    const [hours, minutes] = timeString.split(":").map(Number);

    // Handle invalid time values
    if (isNaN(hours) || isNaN(minutes)) {
      console.error("Invalid time components:", timeString);
      return timeString;
    }

    // For Arabic format
    if (isArabic) {
      const period = hours >= 12 ? "مساءً" : "صباحًا";
      const displayHours = hours % 12 || 12;
      return `${displayHours}:${minutes.toString().padStart(2, "0")} ${period}`;
    }
    // For English format
    else {
      const period = hours >= 12 ? "PM" : "AM";
      const displayHours = hours % 12 || 12;
      return `${displayHours}:${minutes.toString().padStart(2, "0")} ${period}`;
    }
  }

  function getFormattedDate(date) {
    if (!date) return "";
    const options = {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    };
    return date.toLocaleDateString(isArabic ? "ar-SA" : "en-US", options);
  }

  // ============ TIME SLOT GENERATION ============
  function generateTimeSlots(workingHours, visitPeriod, fullyBookedSlots) {
    // Convert working hours to minutes
    const [startHour, startMinute] = workingHours.from.split(":").map(Number);
    const [endHour, endMinute] = workingHours.to.split(":").map(Number);

    // Convert visit period to minutes
    const visitPeriodMinutes = visitPeriod * 60;

    // Calculate total working minutes
    const startMinutes = startHour * 60 + startMinute;
    const endMinutes = endHour * 60 + endMinute;

    const availableSlots = [];
    const bookedSlots = [];

    // Normalize booked slots for easier comparison
    const normalizedBookedSlots = Array.isArray(fullyBookedSlots)
      ? fullyBookedSlots.map((slot) => {
          // Handle different time formats (9:00 vs 09:00)
          const normalizedStartTime = slot.startTime.includes(":")
            ? slot.startTime
            : `${slot.startTime}:00`;

          return {
            ...slot,
            startTime: normalizedStartTime,
            // Convert times to lowercase and trim for consistent comparison
            startTimeNormalized: normalizedStartTime.toLowerCase().trim(),
          };
        })
      : [];

    // Generate time slots based on visit period
    for (
      let currentMinutes = startMinutes;
      currentMinutes + visitPeriodMinutes <= endMinutes;
      currentMinutes += visitPeriodMinutes
    ) {
      // Calculate start and end times
      const startHour = Math.floor(currentMinutes / 60);
      const startMinute = currentMinutes % 60;
      const endCalculatedMinutes = currentMinutes + visitPeriodMinutes;
      const endHour = Math.floor(endCalculatedMinutes / 60);
      const endMinute = endCalculatedMinutes % 60;

      // Format time strings
      const startTime = `${startHour.toString().padStart(2, "0")}:${startMinute
        .toString()
        .padStart(2, "0")}`;
      const endTime = `${endHour.toString().padStart(2, "0")}:${endMinute
        .toString()
        .padStart(2, "0")}`;

      // Check if this slot is fully booked - with improved comparison
      const isBooked = normalizedBookedSlots.some((slot) => {
        // Try different formats of time comparison to ensure matching
        return (
          // Direct comparison
          (slot.startTimeNormalized === startTime.toLowerCase() ||
            // Compare without leading zeros
            slot.startTimeNormalized ===
              `${parseInt(startTime.split(":")[0])}:${
                startTime.split(":")[1]
              }`.toLowerCase() ||
            // Compare with different separators
            slot.startTimeNormalized.replace(":", "") ===
              startTime.replace(":", "").toLowerCase()) &&
          slot.allTechniciansBusy
        );
      });

      const timeSlot = {
        formatted: formatTime(startTime),
        value: startTime,
        endTime: endTime,
        formattedEndTime: formatTime(endTime),
      };

      // Sort into appropriate array based on availability
      if (isBooked) {
        bookedSlots.push(timeSlot);
      } else {
        availableSlots.push(timeSlot);
      }
    }

    return { availableSlots, bookedSlots };
  }

  // ============ API CALLS ============
  const fetchAvailableSlots = async (date) => {
    if (!date || !selectedDepartment) return;

    setIsLoading(true);
    setError(null);

    try {
      const formattedDate = formatDateForAPI(date);

      const response = await api.get(`/bookings/busySlots`, {
        params: {
          date: formattedDate,
          categoryId: selectedDepartment._id,
          addressId: selectedAddress ? selectedAddress._id : null,
        },
      });

      const {
        bookingAvailability,
        workingHours: apiWorkingHours,
        visitPeriod,
        fullyBookedSlots,
      } = response.data.data;

      // Store working hours for further use
      setWorkingHours(apiWorkingHours);

      // Check if today is past working hours
      const today = new Date();
      if (
        date.toDateString() === today.toDateString() &&
        isTimePastWorkingHours(today, apiWorkingHours.to)
      ) {
        setBookingAvailability(false);
        setAvailableTimes([]);
        setBookedTimes([]);
        return;
      }

      // Log the fully booked slots from API

      setBookingAvailability(bookingAvailability);

      // Generate available and booked time slots
      const { availableSlots, bookedSlots } = generateTimeSlots(
        apiWorkingHours,
        visitPeriod,
        fullyBookedSlots
      );
      setAvailableTimes(availableSlots);
      setBookedTimes(bookedSlots);
    } catch (error) {
      
   console.log(error.message);

      setError(error.message);
      setAvailableTimes([]);
      setBookedTimes([]);
      setBookingAvailability(false);
    } finally {
      setIsLoading(false);
    }
  };

  // ============ CALENDAR GENERATION ============
  // In the calendar generation useEffect, modify the isDisabled logic
  useEffect(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();

    // First and last day of the month
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);

    // Adjust for RTL calendar (week starts on Saturday)
    let firstDayOfWeek = (firstDay.getDay() + 1) % 7;

    const days = [];

    // Add empty slots for days before first day of month
    for (let i = 0; i < firstDayOfWeek; i++) {
      days.push({ day: null, date: null });
    }

    // Add days of the month
    for (let i = 1; i <= lastDay.getDate(); i++) {
      const date = new Date(year, month, i);
      const today = new Date();
      // Reset time to midnight for accurate date comparison
      today.setHours(0, 0, 0, 0);
      date.setHours(0, 0, 0, 0);

      // Modify this part to allow selecting the current day
      // Instead of disabling the current day, only disable past dates
      const isPastDate = date < today;

      // Remove the today specific closed hours check
      // Let the time slot fetching handle working hours availability

      days.push({
        day: i,
        date: date,
        isDisabled: isPastDate, // Only disable dates before today
        isToday: date.toDateString() === today.toDateString(),
      });
    }

    setCalendarDays(days);
  }, [currentMonth]);

  // ============ EFFECTS ============
  // Fetch available slots when date changes
  useEffect(() => {
    if (selectedDate) {
      fetchAvailableSlots(selectedDate);
    } else {
      setAvailableTimes([]);
      setBookedTimes([]);
    }
  }, [selectedDate, selectedDepartment]);

  // ============ EVENT HANDLERS ============
  const prevMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1)
    );
  };

  const nextMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1)
    );
  };

  const handleDateClick = (day) => {
    // Require authentication before selecting a date
    if (!isAuthenticated) {
      setShowLoginModal(true);
      return;
    }

    if (day && !day.isDisabled) {
      setSelectedDate(day.date);
      setSelectedTime(null); // Reset time when date changes
    }
  };

  const handleTimeClick = (time) => {
    // Require authentication before selecting a time
    if (!isAuthenticated) {
      setShowLoginModal(true);
      return;
    }

    // Don't allow selecting past time slots
    if (isTimeSlotPast(time)) {
      return;
    }

    setSelectedTime(time);
  };

  const handleLoginClose = () => {
    setShowLoginModal(false);
  };

  const handleConfirmBooking = () => {
    // Final authentication check
    if (!isAuthenticated) {
      setShowLoginModal(true);
      return;
    }

    if (!selectedDate || !selectedTime) return;

    // Create appointment details object
    const appointmentDetails = {
      date: formatDate(selectedDate),
      time: selectedTime.formatted,
      endTime: selectedTime.formattedEndTime,
      department: selectedDepartment
        ? {
            id: selectedDepartment._id,
            name: selectedDepartment.name[language],
          }
        : null,
      services: selectedServices.map((service) => ({
        id: service._id,
        name: service.name[language],
        price: service.price,
      })),
    };

    // Store appointment details and services in context
    setAppointment(appointmentDetails);
    addServices(selectedServices);

    // Close modal and proceed
    onClose();

    // Use callback or navigate directly
    if (typeof onBookingComplete === "function") {
      onBookingComplete();
    } else {
      navigate("/cart");
    }
  };

  // ============ RENDER HELPERS ============
  const renderTimeSelection = () => {
    const formattedFromTime = moment(workingHours.from, "HH:mm").format(
      "h:mm A"
    );
    const formattedToTime = moment(workingHours.to, "HH:mm").format("h:mm A");
    if (isLoading) {
      return <div className="loading-state">{translations.loading}</div>;
    }

    if (error) {
      return <div className="error-state">{error}</div>;
    }

    // Special handling for today being past working hours
    const today = new Date();
    if (
      selectedDate &&
      selectedDate.toDateString() === today.toDateString() &&
      !bookingAvailability
    ) {
      return (
        <div className="working-hours-container">
          <div className="working-hours-header">
            <LockClock size={24} className="working-hours-icon" />
            <h3 className="working-hours-title">{translations.workingHours}</h3>
          </div>
          <div className="working-hours-display">
            <div className="working-hours-time">
              <span className="working-hours-label">
                {isArabic ? "من" : "From"}:
              </span>
              <span className="working-hours-value">{formattedFromTime}</span>
            </div>
            <div className="working-hours-time">
              <span className="working-hours-label">
                {isArabic ? "إلى" : "To"}:
              </span>
              <span className="working-hours-value">{formattedToTime}</span>
            </div>
          </div>
          <div className="no-availability">
            {translations.noAvailabilityToday}
          </div>
        </div>
      );
    }

    if (!bookingAvailability) {
      return (
        <div className="no-availability">{translations.noAvailability}</div>
      );
    }

    return (
      <div className="time-selection">
        {/* Section Title */}
        <h4 className="field-label">
          {selectedDate
            ? translations.availableTimeLabel
            : translations.pleaseSelectDate}
        </h4>

        {/* Available time slots */}
        {availableTimes.length > 0 && (
          <div className="available-slots">
            <h5 className="slots-heading">
              {isArabic ? "المواعيد المتاحة" : "Available Slots"}
            </h5>
            <div className="time-grid">
              {availableTimes.map((time) => {
                const isPastTimeSlot = isTimeSlotPast(time);
                return (
                  <button
                    key={time.value}
                    className={`time-option ${
                      selectedTime && selectedTime.value === time.value
                        ? "selected"
                        : ""
                    } ${isPastTimeSlot ? "past-time" : ""}`}
                    onClick={() => handleTimeClick(time)}
                    aria-selected={
                      selectedTime && selectedTime.value === time.value
                    }
                    disabled={isPastTimeSlot}
                    aria-disabled={isPastTimeSlot}
                  >
                    <>
                      <span className="time-range">
                        {isArabic
                          ? `من ${time.formatted} إلى ${time.formattedEndTime}`
                          : `${time.formatted} - ${time.formattedEndTime}`}
                      </span>
                      {isPastTimeSlot && (
                        <span className="time-status no-wrap">
                          {translations.pastTimeStatus}
                        </span>
                      )}
                    </>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Fully booked time slots */}
        {bookedTimes.length > 0 && (
          <div className="booked-slots">
            <h5 className="slots-heading">{translations.fullyBookedLabel}</h5>
            <div className="time-grid booked-grid">
              {bookedTimes.map((time) => {
                const isPastTimeSlot = isTimeSlotPast(time);
                return (
                  <button
                    key={time.value}
                    className={`time-option booked ${
                      isPastTimeSlot ? "past-time" : ""
                    }`}
                    disabled
                    aria-disabled="true"
                  >
                    <>
                      <span className="time-range">
                        {isArabic
                          ? `من ${time.formatted} إلى ${time.formattedEndTime}`
                          : `${time.formatted} - ${time.formattedEndTime}`}
                      </span>
                      {isPastTimeSlot ? (
                        <span className="time-status no-wrap">
                          {translations.pastTimeStatus}
                        </span>
                      ) : (
                        <span className="time-status no-wrap">
                          {translations.bookedStatus}
                        </span>
                      )}
                    </>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {availableTimes.length === 0 &&
          bookedTimes.length === 0 &&
          !isLoading &&
          !error &&
          selectedDate && (
            <div className="no-times">
              {isArabic
                ? "لا توجد مواعيد متاحة في هذا اليوم. يرجى اختيار يوم آخر."
                : "No times available for this day. Please select another date."}
            </div>
          )}
      </div>
    );
  };

  // Don't render if modal is closed
  if (!open) return null;

  // Get confirmation text for selected date and time
  const confirmationText =
    selectedDate && selectedTime
      ? isArabic
        ? `سيتم تأكيد حجزك في يوم ${getFormattedDate(selectedDate)} من الساعة ${
            selectedTime.formatted
          } إلى ${selectedTime.formattedEndTime}`
        : `Your booking will be confirmed for ${selectedDate.toLocaleDateString(
            "en-US",
            {
              weekday: "long",
              month: "long",
              day: "numeric",
            }
          )} from ${selectedTime.formatted} to ${selectedTime.formattedEndTime}`
      : "";

  // ============ RENDER ============
  return (
    <>
      <div className="modal-overlay" onClick={onClose}>
        <div
          className="booking-modal"
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-labelledby="booking-modal-title"
        >
          <div className="modal-header">
            <h2 className="modal-title" id="booking-modal-title">
              {translations.modalTitle}
            </h2>
            <button
              className="close-button"
              onClick={onClose}
              aria-label={translations.cancelButtonLabel}
            >
              <span>×</span>
            </button>
          </div>

          <div className="booking-content">
            <div className="date-time-selection">
              <h3 className="section-title">
                {translations.dateSelectionTitle}
              </h3>

              {/* Calendar */}
              <div
                className="custom-calendar"
                role="application"
                aria-label="Calendar"
              >
                <div className="calendar-header">
                  <button
                    className="month-nav"
                    onClick={prevMonth}
                    aria-label={isArabic ? "الشهر السابق" : "Previous month"}
                  >
                    &#9656;
                  </button>
                  <h4 className="current-month">
                    {monthNames[currentMonth.getMonth()]}{" "}
                    {currentMonth.getFullYear()}
                  </h4>
                  <button
                    className="month-nav"
                    onClick={nextMonth}
                    aria-label={isArabic ? "الشهر التالي" : "Next month"}
                  >
                    &#9666;
                  </button>
                </div>

                <div className="calendar-weekdays" role="row">
                  {weekDays.map((day) => (
                    <div key={day} className="weekday" role="columnheader">
                      {day}
                    </div>
                  ))}
                </div>

                <div className="calendar-days" role="grid">
                  {calendarDays.map((day, index) => (
                    <div
                      key={index}
                      className={`calendar-day ${!day.day ? "empty" : ""} ${
                        day.isDisabled ? "disabled" : ""
                      } ${day.isToday ? "today" : ""} ${
                        selectedDate &&
                        day.date &&
                        day.date.toDateString() === selectedDate.toDateString()
                          ? "selected"
                          : ""
                      }`}
                      onClick={() => handleDateClick(day)}
                      tabIndex={day.day && !day.isDisabled ? 0 : -1}
                      role={day.day ? "gridcell" : "presentation"}
                      aria-selected={
                        selectedDate &&
                        day.date &&
                        day.date.toDateString() === selectedDate.toDateString()
                      }
                      aria-disabled={day.isDisabled}
                    >
                      {day.day}
                    </div>
                  ))}
                </div>
              </div>

              {/* Time Selection */}
              {renderTimeSelection()}

              {/* Booking Confirmation */}
              {selectedDate && selectedTime && (
                <div className="booking-confirmation" aria-live="polite">
                  <p className="confirmation-text">{confirmationText}</p>
                </div>
              )}
            </div>
          </div>

          <div className="modal-actions">
            <button className="cancel-btn" onClick={onClose}>
              {translations.cancelButtonLabel}
            </button>
            <button
              className={`confirm-btn ${
                !selectedDate || !selectedTime ? "disabled" : ""
              }`}
              disabled={!selectedDate || !selectedTime}
              onClick={handleConfirmBooking}
            >
              {translations.confirmButtonLabel}
            </button>
          </div>
        </div>
      </div>

      {/* Login Modal */}
      {showLoginModal && (
        <Login open={showLoginModal} onClose={handleLoginClose} />
      )}
    </>
  );
};

export default BookingModal;
